import { useState, useCallback } from "react";
import {
  CheckCircle2, XCircle, AlertTriangle,
  ChevronDown, ChevronUp, Sparkles, RefreshCw
} from "lucide-react";

function auditExercise(ex) {
  const d    = ex.data || {};
  const type = ex.exercise_type;
  const issues = [];

  const dataStr = JSON.stringify(d).toLowerCase();
  if (dataStr.includes('"pending"') || dataStr.includes("pending —")) {
    return { status: "pending", issues: ["Sin contenido real — aún en Pending"] };
  }

  switch (type) {
    case "conversation_spacy_prompt":
      if (!Array.isArray(d.learning_objectives) || d.learning_objectives.length < 4)
        issues.push("learning_objectives necesita ≥4 items");
      if (!Array.isArray(d.visual_gallery_cards) || d.visual_gallery_cards.length < 1)
        issues.push("visual_gallery_cards necesita ≥1 tarjeta");
      if (!d.lesson_introduction || d.lesson_introduction.length < 20)
        issues.push("lesson_introduction muy corta o ausente");
      break;

    case "grammar_notes":
      if (!Array.isArray(d.points) || d.points.length < 3)
        issues.push(`points necesita ≥3 items (tiene ${(d.points||[]).length})`);
      (d.points||[]).forEach((p,i) => {
        if (!p.header) issues.push(`points[${i}]: falta header`);
        if (!p.content || p.content.length < 30) issues.push(`points[${i}]: content muy corto`);
      });
      break;

    case "quiz":
      if (!Array.isArray(d.questions) || d.questions.length < 5)
        issues.push(`questions necesita ≥5 items (tiene ${(d.questions||[]).length})`);
      (d.questions||[]).forEach((q,i) => {
        if (!q.answer || typeof q.answer === "number")
          issues.push(`questions[${i}]: answer debe ser texto, no índice`);
        if (q.answer && Array.isArray(q.options) && !q.options.includes(q.answer))
          issues.push(`questions[${i}]: answer "${q.answer}" no está en options`);
      });
      break;

    case "fill_in_the_blank":
      if (!Array.isArray(d.blanks) || d.blanks.length < 5)
        issues.push(`blanks necesita ≥5 items (tiene ${(d.blanks||[]).length})`);
      (d.blanks||[]).forEach((b,i) => {
        if (!b.before_blank) issues.push(`blanks[${i}]: falta before_blank`);
        if (!b.answer)       issues.push(`blanks[${i}]: falta answer`);
        if (!b.hint)         issues.push(`blanks[${i}]: falta hint`);
      });
      break;

    case "matching":
      if (!Array.isArray(d.pairs)   || d.pairs.length   < 4) issues.push("pairs necesita ≥4 items");
      if (!Array.isArray(d.matches) || d.matches.length < 4) issues.push("matches necesita ≥4 items");
      if (!d.solution || typeof d.solution !== "object") {
        issues.push("Falta solution");
      } else {
        const pairIds  = (d.pairs  ||[]).map(p => p.id);
        const matchIds = (d.matches||[]).map(m => m.id);
        const entries  = Object.entries(d.solution);
        const isOneToOne = entries.length > 1 && entries.every(([k,v]) => {
          return pairIds.indexOf(k) === matchIds.indexOf(v);
        });
        if (isOneToOne) issues.push("solution parece 1→1 — mezcla el orden");
        entries.forEach(([k,v]) => {
          if (!pairIds.includes(k))  issues.push(`solution: key "${k}" no existe en pairs`);
          if (!matchIds.includes(v)) issues.push(`solution: value "${v}" no existe en matches`);
        });
      }
      break;

    case "listening":
      if (!d.audio_url || d.audio_url.includes("pending"))
        issues.push("audio_url pendiente — reemplaza con URL real de Cloudinary");
      if (!d.transcript || d.transcript.length < 80)
        issues.push("transcript muy corto o ausente");
      if (!Array.isArray(d.questions) || d.questions.length < 5)
        issues.push(`questions necesita ≥5 items (tiene ${(d.questions||[]).length})`);
      (d.questions||[]).forEach((q,i) => {
        if (q.answer && Array.isArray(q.options) && !q.options.includes(q.answer))
          issues.push(`questions[${i}]: answer no está en options`);
      });
      break;

    case "pronunciation":
      if (!Array.isArray(d.words_or_sentences) || d.words_or_sentences.length < 8)
        issues.push(`words_or_sentences necesita ≥8 items (tiene ${(d.words_or_sentences||[]).length})`);
      (d.words_or_sentences||[]).forEach((w,i) => {
        if (!w.phonetic || !w.phonetic.startsWith("/"))
          issues.push(`item[${i}]: phonetic debe empezar con /`);
      });
      break;

    case "error_detection":
      if (!Array.isArray(d.sentences) || d.sentences.length < 5)
        issues.push(`sentences necesita ≥5 items (tiene ${(d.sentences||[]).length})`);
      (d.sentences||[]).forEach((s,i) => {
        if (s.answer !== "Correct" && s.answer !== "Incorrect")
          issues.push(`sentences[${i}]: answer debe ser "Correct" o "Incorrect" exacto`);
      });
      if ((d.sentences||[]).filter(s => s.answer === "Correct").length   === 0)
        issues.push("Todas son Incorrect — agrega al menos 2 Correct");
      if ((d.sentences||[]).filter(s => s.answer === "Incorrect").length === 0)
        issues.push("Todas son Correct — agrega al menos 3 Incorrect");
      break;

    case "reading":
      if (!d.text || d.text.length < 150)
        issues.push(`text necesita ≥150 chars (tiene ${(d.text||"").length})`);
      if (!d.comprehension_quiz?.questions?.length || d.comprehension_quiz.questions.length < 5)
        issues.push("comprehension_quiz necesita ≥5 preguntas");
      (d.comprehension_quiz?.questions||[]).forEach((q,i) => {
        if (q.answer && Array.isArray(q.options) && !q.options.includes(q.answer))
          issues.push(`question[${i}]: answer no está en options`);
      });
      break;

    case "writing":
      if (!d.theme       || d.theme.length       < 20) issues.push("theme muy corto o ausente");
      if (!d.instruction || d.instruction.length < 30) issues.push("instruction muy corta");
      break;

    default:
      issues.push(`Tipo desconocido: ${type}`);
  }

  return issues.length === 0
    ? { status: "ok",    issues: [] }
    : { status: "error", issues };
}

const EX_META = {
  conversation_spacy_prompt: { emoji: "🤖", label: "Spacy Prompt",    color: "#00FFFF" },
  grammar_notes:             { emoji: "📖", label: "Grammar Notes",   color: "#6366f1" },
  quiz:                      { emoji: "❓", label: "Quiz",            color: "#06b6d4" },
  fill_in_the_blank:         { emoji: "✏️", label: "Fill in Blank",   color: "#10b981" },
  matching:                  { emoji: "🔗", label: "Matching",        color: "#f59e0b" },
  listening:                 { emoji: "🎧", label: "Listening",       color: "#ec4899" },
  pronunciation:             { emoji: "🎙️", label: "Pronunciation",   color: "#f97316" },
  error_detection:           { emoji: "🔍", label: "Error Detection", color: "#ef4444" },
  reading:                   { emoji: "📚", label: "Reading",         color: "#8b5cf6" },
  writing:                   { emoji: "✍️", label: "Writing",         color: "#14b8a6" },
};

export default function LessonAudit({ exercises, t, dark, HICOLOR }) {
  const [panelOpen,    setPanelOpen]    = useState(false);
  const [results,      setResults]      = useState(null);
  const [auditing,     setAuditing]     = useState(false);
  // ✅ UN SOLO estado para controlar qué filas están expandidas — sin subcomponentes
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = useCallback((id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const runAudit = useCallback(() => {
    setAuditing(true);
    setExpandedRows({});
    setTimeout(() => {
      const audited = (exercises || []).map(ex => ({
        id:   ex.id,
        type: ex.exercise_type,
        pos:  ex.position,
        ...auditExercise(ex)
      }));
      setResults(audited);
      setAuditing(false);
      setPanelOpen(true);
    }, 400);
  }, [exercises]);

  const okCount      = (results||[]).filter(r => r.status === "ok").length;
  const pendingCount = (results||[]).filter(r => r.status === "pending").length;
  const errorCount   = (results||[]).filter(r => r.status === "error").length;
  const total        = (results||[]).length;
  const allGreen     = results && errorCount === 0 && pendingCount === 0;

  return (
    <div style={{ marginBottom: 10 }}>

      {/* Botón Auditar */}
      <button
        onClick={runAudit}
        disabled={auditing || !exercises?.length}
        style={{
          width: "100%",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          padding: "11px", borderRadius: 12,
          border: `1.5px solid ${allGreen ? t.ok : HICOLOR}44`,
          cursor: auditing ? "wait" : "pointer",
          background: allGreen
            ? (dark ? `${t.ok}14` : `${t.ok}0a`)
            : (dark ? `${HICOLOR}14` : `${HICOLOR}0a`),
          color: allGreen ? t.ok : HICOLOR,
          fontSize: 12, fontWeight: 800, fontFamily: "Inter,sans-serif",
          transition: "all .2s",
          opacity: !exercises?.length ? .4 : 1,
        }}>
        {auditing
          ? <><RefreshCw size={13} style={{ animation: "cmsSpin .7s linear infinite" }}/> Auditando...</>
          : <><Sparkles size={13}/> Auditar Lección — Checklist Cambridge</>
        }
      </button>

      {/* Panel de resultados */}
      {results && panelOpen && (
        <div style={{
          marginTop: 8, borderRadius: 14, overflow: "hidden",
          border: `1px solid ${allGreen ? t.ok : (errorCount > 0 ? t.er : t.wrn)}33`,
          background: t.bg,
          boxShadow: `inset -2px 2px 5px ${t.sd},inset 2px -2px 5px ${t.sl}`,
          animation: "cmsFadeUp .2s ease"
        }}>

          {/* Header */}
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 14px",
            borderBottom: `1px solid ${dark ? "rgba(255,255,255,.05)" : "rgba(0,0,0,.05)"}`,
            background: allGreen
              ? (dark ? `${t.ok}0e` : `${t.ok}06`)
              : (dark ? `${t.er}0e` : `${t.er}06`)
          }}>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: 12, fontWeight: 800,
                color: allGreen ? t.ok : (errorCount > 0 ? t.er : t.wrn)
              }}>
                {allGreen
                  ? "✅ Lección lista para producción"
                  : `⚠️ ${errorCount + pendingCount} ejercicio(s) necesitan atención`}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                {[
                  { label: "Listos",  count: okCount,      color: t.ok  },
                  { label: "Pending", count: pendingCount, color: t.wrn },
                  { label: "Errores", count: errorCount,   color: t.er  },
                ].map(b => (
                  <span key={b.label} style={{
                    fontSize: 8, fontWeight: 700,
                    padding: "2px 7px", borderRadius: 50,
                    background: b.color + "18", color: b.color,
                    fontFamily: "JetBrains Mono,monospace"
                  }}>{b.count} {b.label}</span>
                ))}
              </div>
            </div>

            {/* Barra de progreso */}
            <div style={{
              width: 80, height: 6, borderRadius: 3,
              background: dark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.08)",
              overflow: "hidden", flexShrink: 0
            }}>
              <div style={{
                height: "100%",
                width: `${total > 0 ? (okCount / total) * 100 : 0}%`,
                borderRadius: 3,
                background: allGreen
                  ? `linear-gradient(90deg,${HICOLOR},${t.ok})`
                  : `linear-gradient(90deg,${HICOLOR},${t.wrn})`,
                transition: "width .5s cubic-bezier(.4,0,.2,1)"
              }}/>
            </div>

            <button onClick={() => setPanelOpen(false)} style={{
              background: "transparent", border: "none",
              cursor: "pointer", color: t.tx2,
              display: "flex", alignItems: "center"
            }}>
              <ChevronUp size={13}/>
            </button>
          </div>

          {/* Lista de ejercicios */}
          <div style={{ padding: "8px 10px", display: "flex", flexDirection: "column", gap: 4 }}>
            {results.map(r => {
              const meta = EX_META[r.type] || { emoji: "📄", label: r.type, color: HICOLOR };
              const statusColor = r.status === "ok" ? t.ok : r.status === "pending" ? t.wrn : t.er;
              const isRowOpen = !!expandedRows[r.id];

              return (
                <div key={r.id} style={{
                  borderRadius: 10,
                  border: `1px solid ${statusColor}22`,
                  background: dark ? `${statusColor}06` : `${statusColor}03`,
                  overflow: "hidden"
                }}>
                  <div
                    onClick={() => r.issues.length > 0 && toggleRow(r.id)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      padding: "7px 10px",
                      cursor: r.issues.length > 0 ? "pointer" : "default"
                    }}>

                    <div style={{
                      width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 8, fontWeight: 800,
                      background: meta.color + "18", color: meta.color,
                      fontFamily: "JetBrains Mono,monospace"
                    }}>{r.pos}</div>

                    <span style={{ fontSize: 13, flexShrink: 0 }}>{meta.emoji}</span>

                    <span style={{
                      fontSize: 10, fontWeight: 600, color: t.tx,
                      flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
                    }}>
                      {meta.label}
                      {r.status === "pending" && (
                        <span style={{
                          marginLeft: 6, fontSize: 8,
                          color: t.wrn, fontFamily: "JetBrains Mono,monospace"
                        }}>· SIN CONTENIDO</span>
                      )}
                    </span>

                    {r.status === "ok" && (
                      <CheckCircle2 size={14} style={{ color: t.ok, flexShrink: 0 }}/>
                    )}
                    {r.status === "pending" && (
                      <AlertTriangle size={13} style={{ color: t.wrn, flexShrink: 0 }}/>
                    )}
                    {r.status === "error" && (
                      <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
                        <span style={{
                          fontSize: 8, fontWeight: 700,
                          background: t.er + "18", color: t.er,
                          padding: "1px 6px", borderRadius: 50,
                          fontFamily: "JetBrains Mono,monospace"
                        }}>
                          {r.issues.length} error{r.issues.length > 1 ? "es" : ""}
                        </span>
                        {isRowOpen
                          ? <ChevronUp   size={11} style={{ color: t.er }}/>
                          : <ChevronDown size={11} style={{ color: t.er }}/>
                        }
                      </div>
                    )}
                  </div>

                  {/* Errores expandidos */}
                  {isRowOpen && r.issues.length > 0 && (
                    <div style={{
                      padding: "0 10px 8px 36px",
                      display: "flex", flexDirection: "column", gap: 3
                    }}>
                      {r.issues.map((issue, i) => (
                        <div key={i} style={{
                          display: "flex", gap: 6, alignItems: "flex-start",
                          padding: "4px 8px", borderRadius: 7,
                          background: `${t.er}0e`, border: `1px solid ${t.er}22`
                        }}>
                          <XCircle size={10} style={{ color: t.er, flexShrink: 0, marginTop: 1 }}/>
                          <span style={{
                            fontSize: 9, color: t.er,
                            fontFamily: "JetBrains Mono,monospace", lineHeight: 1.5
                          }}>{issue}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          {allGreen && (
            <div style={{
              padding: "8px 14px",
              borderTop: `1px solid ${t.ok}22`,
              background: dark ? `${t.ok}0e` : `${t.ok}06`,
              fontSize: 10, color: t.ok, fontWeight: 600,
              display: "flex", alignItems: "center", gap: 6
            }}>
              <CheckCircle2 size={12}/>
              Los 10 ejercicios tienen estructura Cambridge válida.
              El Spacy Mirror se guarda desde el card 11.
            </div>
          )}
          {!allGreen && (errorCount > 0 || pendingCount > 0) && (
            <div style={{
              padding: "8px 14px",
              borderTop: `1px solid ${t.tx2}22`,
              fontSize: 9, color: t.tx2, lineHeight: 1.6
            }}>
              Corrige los marcados → Editar → pega el JSON → Guardar → vuelve a Auditar.
            </div>
          )}
        </div>
      )}
    </div>
  );
}